import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui';
import { usePlan } from '../../hooks/usePlan';

export default function AdminSurveys() {
  const { canCreateSurvey, isFreePlan, remainingSurveys } = usePlan();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ status: '', from_date: '', to_date: '', page: 1 });

  const loadSurveys = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.from_date) params.set('from_date', filters.from_date);
    if (filters.to_date) params.set('to_date', filters.to_date);
    params.set('page', filters.page);
    api.get(`/admin/surveys?${params}`)
      .then((res) => {
        setSurveys(res.data?.data || []);
        setMeta(res.data?.meta || null);
      })
      .catch(() => {
        setSurveys([]);
        setMeta(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    loadSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.from_date, filters.to_date, filters.page]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Micro-sondages</h1>
        {canCreateSurvey ? (
          <Link to="/admin/surveys/new">
            <Button>Nouveau sondage</Button>
          </Link>
        ) : (
          <div className="flex items-center gap-4">
            <p className="text-amber-700 text-sm">
              {isFreePlan && remainingSurveys === 0
                ? "Vous avez utilisé votre sondage gratuit. Passez à l'abonnement pour continuer."
                : 'Limite atteinte.'}
            </p>
            <Link to="/pricing">
              <Button>Passer à l'abonnement</Button>
            </Link>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-4 mb-4">
        <div>
          <label htmlFor="filter-status" className="sr-only">Statut</label>
          <select
            id="filter-status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
            className="px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg text-sm"
            aria-label="Filtrer par statut"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="active">Actif</option>
            <option value="closed">Clôturé</option>
          </select>
        </div>
        <div>
          <label htmlFor="filter-from" className="sr-only">À partir du</label>
          <input
            id="filter-from"
            type="date"
            value={filters.from_date}
            onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value, page: 1 }))}
            className="px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg text-sm"
            aria-label="Date de début"
          />
        </div>
        <div>
          <label htmlFor="filter-to" className="sr-only">Jusqu'au</label>
          <input
            id="filter-to"
            type="date"
            value={filters.to_date}
            onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value, page: 1 }))}
            className="px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg text-sm"
            aria-label="Date de fin"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Chargement...</p>
      ) : surveys.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <p>Aucun sondage pour le moment.</p>
          <p className="text-sm mt-2">Créez votre premier micro-sondage.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {surveys.map((s) => (
                <tr key={s.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{s.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{s.status}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {s.date_start} — {s.date_end}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {s.participations ?? 0} ({s.participation_rate ?? 0}%)
                  </td>
                  <td className="px-6 py-4 flex gap-2">
                    <Link to={`/admin/surveys/${s.id}`} className="text-primary-600 hover:underline text-sm">
                      Détail
                    </Link>
                    <Link to={`/admin/surveys/${s.id}/report`} className="text-primary-600 hover:underline text-sm">
                      Rapport
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {meta && meta.last_page > 1 && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Page {meta.current_page} / {meta.last_page} ({meta.total} sondages)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
                  disabled={meta.current_page <= 1}
                  aria-label="Page précédente"
                  className="min-w-[44px] min-h-[44px] px-3 py-2 text-sm border rounded disabled:opacity-50 focus:ring-2 focus:ring-primary-500"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, page: Math.min(meta.last_page, f.page + 1) }))}
                  disabled={meta.current_page >= meta.last_page}
                  aria-label="Page suivante"
                  className="min-w-[44px] min-h-[44px] px-3 py-2 text-sm border rounded disabled:opacity-50 focus:ring-2 focus:ring-primary-500"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
